<template>
  <div
    class="Whiteboard"
    :class="{
      'm-black': hasBackgroundImage,
      'm-iframe': $store.state.isInIframe,
    }"
  >
    <div
      :class="{
        wbroot: true,
        'm-loading': !isLoaded,
        'm-black': hasBackgroundImage,
      }"
      ref="wbroot"
      v-on:wheel="onWheel"
    ></div>

    <div v-if="!isLoaded" class="LoadingIndicator">
      <LoadingSpinner></LoadingSpinner>
    </div>

    <DropTarget v-if="$store.state.dragState === 'dragging'"></DropTarget>

    <ActiveUserList v-if="!$store.state.isInIframe"></ActiveUserList>
    <TopToolbarStack>
      <TopBar
        v-if="!$store.getters.hasFolder && !$store.state.isInIframe"
        v-on:editname="maybeOpenGuestUsernameModal"
      ></TopBar>
      <AuxHUD
        v-if="canWrite"
        v-on:deleteSelection="deleteSelection"
        v-on:duplicateSelectedShapes="duplicateSelectedShapes"
        v-on:undo="undo"
        v-on:redo="redo"
      ></AuxHUD>
    </TopToolbarStack>
    <ToolPickerHUD v-if="canWrite" :config="config"></ToolPickerHUD>
    <ZoomControls
      v-if="!hasBackgroundImage"
      v-on:setScale="setScale"
    ></ZoomControls>

    <AllModals v-on:clearWhiteboard="clearWhiteboard"></AllModals>

    <a
      v-if="state.featureFlags.feedback_prompt && !$store.state.isInIframe"
      class="CircleShadowButton m-variable-width FeedbackButton DesktopOnly"
      target="_"
      href="https://docs.google.com/forms/d/e/1FAIpQLSd9x3Cg0OzgSQD6Cv1t1OHp48Fa2x2B3JxrDl4W2ZFHJRwQpA/viewform?usp=sf_link"
      >Send feedback</a
    >

    <OfflineIndicator v-if="canWrite" />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Ref, Watch } from "vue-property-decorator";
import WBComponent from "@/WBComponent";
import Mousetrap from "mousetrap";

import "@/wbcontroller/wb.scss";

import AllModals from "@/components/modals/AllModals.vue";
import ActiveUserList from "@/components/ActiveUserList.vue";
import LoadingSpinner from "@/components/ui/LoadingSpinner.vue";
import OfflineIndicator from "@/components/OfflineIndicator.vue";
import ToolPickerHUD from "@/components/ToolPickerHUD.vue";
import TopBar from "@/components/TopNavBar.vue";
import TopToolbarStack from "@/components/TopToolbarStack.vue";
import AuxHUD from "@/components/AuxHUD.vue";
import ZoomControls from "@/components/ZoomControls.vue";

import { WBState, WhiteboardData } from "@/store/stateInterfaceType";
import { ModalType } from "@/store/ModalType";

import DropTarget from "@/components/DropTarget.vue";
import {
  WhiteboardController,
  WhiteboardControllerDelegate,
} from "@/wbcontroller/WhiteboardController";
import ToolType from "@/wbcontroller/types/ToolType";
import { UserInfo } from "@/wbcontroller/types/UserInfo";
import { WBSelection } from "@/wbcontroller/types/WBSelection";
import { WBOperationManagerState } from "../wbcontroller/operations/OperationManager";
import WhiteboardConfig from "@/WhiteboardConfig";
import { ToolHUDItem } from "@/toolHUDItems";
import { DragState } from "@/wbcontroller/ImageUploadController";
import { PDFInfo } from "@/wbcontroller/PDFController";
import { ModalMutations } from "@/store/modules/modal";
import { GlobalMutations } from "@/store/mutations";
import { ReportedMutations } from "@/store/modules/reported";
import handleIframeMessage from "@/wbcontroller/IframeMessages";

@Component({
  components: {
    ActiveUserList,
    AllModals,
    AuxHUD,
    DropTarget,
    LoadingSpinner,
    OfflineIndicator,
    ToolPickerHUD,
    TopBar,
    TopToolbarStack,
    ZoomControls,
  },
})
export default class Whiteboard
  extends WBComponent
  implements WhiteboardControllerDelegate
{
  @Prop() config!: WhiteboardConfig;

  get wbid(): string {
    return this.state.whiteboard.wbid;
  }

  @Ref() private wbroot!: HTMLDivElement;

  get canWrite(): boolean {
    return (
      this.state.key.accessLevel === "admin" ||
      (this.state.key.accessLevel === "contribute" && !this.state.isLocked)
    );
  }

  isLoaded = false;
  isShiftDown = false;
  isModDown = false;

  private makeWhiteboardController(
    data: WhiteboardData,
    accessKey: { id: string; accessLevel: "admin" | "contribute" | "view" }
  ): WhiteboardController {
    return new WhiteboardController(
      this.wbroot,
      data.wbid,
      this.config.session,
      accessKey,
      this.state.user.isAnonymous,
      this.state.user.username,
      this.config.websocketServerURL,
      null,
      this.state.hud,
      this.state.toolConfig.renderConfig,
      this
    );
  }

  mounted() {
    const state = this.state as WBState;
    const wb = this.makeWhiteboardController(state.whiteboard, state.key);

    GlobalMutations.storeWhiteboardController(this.$store.commit, wb);

    window.addEventListener("message", this.onIframeMessage, false);

    wb.imageUploadController?.state.stream.onValue(this.handleDragStateChanged);

    GlobalMutations.applyNavigation(
      this.$store.commit,
      this.$route.params.wbid
    );
    wb.loadingPromise.then(() => {
      this.isLoaded = true;
    });

    this.bindHotkeys();

    wb.camera.scaleStream.onValue(this.storeScale);
  }

  beforeDestroy() {
    window.removeEventListener("message", this.onIframeMessage);

    this.wbcontroller?.imageUploadController?.state.stream.offValue(
      this.handleDragStateChanged
    );
    this.wbcontroller?.unmount();
    GlobalMutations.removeWhiteboardController(this.$store.commit, this.wbid);
    this.unbindHotkeys();
  }

  _boundKeys?: string[];

  bindHotkeys() {
    const _bind = (
      k: string,
      callback: (e: Mousetrap.ExtendedKeyboardEvent, combo: string) => void,
      event?: string
    ) => {
      this._boundKeys = this._boundKeys || [];
      this._boundKeys.push(k);
      Mousetrap.bind(k, callback, event);
    };

    _bind("mod+shift+z", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.redo();
    });
    _bind("mod+z", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.undo();
    });
    _bind("mod+d", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.duplicateSelectedShapes();
    });
    _bind("space", () => this.wbcontroller?.pushTool(ToolType.Pan), "keydown");
    _bind("space", () => this.wbcontroller?.popToolType(ToolType.Pan), "keyup");
    _bind("esc", () => this.handleEsc());
    _bind("shift", () => this.handleShift(true), "keydown");
    _bind("shift", () => this.handleShift(false), "keyup");
    _bind("mod", () => this.handleMod(true), "keydown");
    _bind("mod", () => this.handleMod(false), "keyup");

    (this.$store.getters.tools as ToolHUDItem[]).forEach((t) => {
      if (!t.hotkey) return;
      if (t.toolType === ToolType.ImageLibrary) {
        // special case
        _bind(t.hotkey, () =>
          ModalMutations.openModal(this.$store.commit, ModalType.ImageLibrary)
        );
      } else {
        _bind(t.hotkey, () =>
          GlobalMutations.setTool(this.$store.commit, t.toolType)
        );
      }
    });
  }

  unbindHotkeys() {
    if (!this._boundKeys) return;
    for (const k of this._boundKeys) {
      Mousetrap.unbind(k);
    }
  }

  onIframeMessage(e: MessageEvent) {
    if (!this.wbcontroller) return;
    handleIframeMessage(e, this.wbcontroller, this.$store);
  }

  clearWhiteboard() {
    this.wbcontroller?.clear();
  }

  handleShift(isShiftDown: boolean) {
    this.isShiftDown = isShiftDown;
    this.wbcontroller?.setIsShiftDown(isShiftDown);
  }

  handleMod(isModDown: boolean) {
    this.isModDown = isModDown;
  }

  handleEsc() {
    if (this.state.modal.displayedModals.size > 0) {
      ModalMutations.closeModals(this.$store.commit);
    }
  }

  handleDragStateChanged(dragState: DragState) {
    ReportedMutations.setDragState(this.$store.commit, dragState);
  }

  onWheel(e: WheelEvent) {
    if (this.isModDown || e.ctrlKey) {
      this.wbcontroller?.zoomUsingEvent(e);
    } else {
      this.wbcontroller?.scrollUsingEvent(e);
    }
  }

  // begin WhiteboardControllerDelegate

  onToolChanged = (toolType: ToolType) => {
    GlobalMutations.setTool(this.$store.commit, toolType);
  };

  onUsersChanged = (map: Record<string, UserInfo>) => {
    GlobalMutations.setActiveUsers(this.$store.commit, map);
  };

  onSelectionsChanged = (selections: WBSelection[]) => {
    GlobalMutations.setSelections(this.$store.commit, selections);
  };

  onUndoStateChanged = (state: WBOperationManagerState) => {
    ReportedMutations.replaceUndoState(this.$store.commit, state);
  };

  onOfflineStateChanged = (state: boolean) => {
    ReportedMutations.setOfflineState(this.$store.commit, state);
  };

  onIsLockedChanged = (value: boolean) => {
    GlobalMutations.setIsLocked(this.$store.commit, value);
  };

  onTriedToUploadImageButDisallowed = () => {
    ModalMutations.openModal(
      this.$store.commit,
      ModalType.FeatureGateForImageUpload
    );
  };

  onRequestInsertPDF = (info: PDFInfo) => {
    GlobalMutations.requestInsertPDF(this.$store.commit, info);
    ModalMutations.openModal(this.$store.commit, ModalType.PDFImport);
  };

  // end WhiteboardControllerDelegate

  maybeOpenGuestUsernameModal() {
    if (this.config.session.is_guest) {
      ModalMutations.openModal(this.$store.commit, ModalType.GuestUsername);
    }
  }

  deleteSelection() {
    this.wbcontroller?.deleteSelections();
  }

  undo() {
    this.wbcontroller?.undo();
  }

  redo() {
    this.wbcontroller?.redo();
  }

  duplicateSelectedShapes() {
    this.wbcontroller?.duplicateSelectedShapes();
  }

  storeScale(scale: number) {
    ReportedMutations.setScale(this.$store.commit, scale);
  }

  setScale(scale: number) {
    if (!this.wbcontroller) return;
    this.wbcontroller.camera.setScale(scale);
  }

  get hasBackgroundImage(): boolean {
    return !!this.$store.getters.backgroundImage;
  }

  get whiteboardName(): string {
    return this.state.whiteboard.name;
  }

  @Watch("wbid")
  wbidChanged(newWbid: string, oldWbid: string) {
    console.log("Change", oldWbid, newWbid);
    const state = this.state as WBState;

    if (!state.folder) return;
    if (oldWbid) {
      this.wbcontroller?.unmount();
      GlobalMutations.removeWhiteboardController(this.$store.commit, oldWbid);
    }

    const newWB = state.folder.whiteboards.filter(
      (wb: WhiteboardData) => wb.wbid === newWbid
    )[0];
    const newController = this.makeWhiteboardController(newWB, state.key);
    GlobalMutations.storeWhiteboardController(
      this.$store.commit,
      newController
    );
    newController.loadingPromise.then(() => {
      this.isLoaded = true;
    });
  }

  @Watch("$route")
  routeChanged(to: { params: { wbid: string } }) {
    GlobalMutations.applyNavigation(this.$store.commit, to.params.wbid);
  }

  @Watch("$store.state.hud.opacity")
  opacityChanged() {
    this.wbcontroller?.settingsDidChange(this.state.hud);
  }

  @Watch("$store.state.hud.strokeColor")
  strokeColorChanged() {
    this.wbcontroller?.settingsDidChange(this.state.hud);
  }

  @Watch("$store.state.hud.fillColor")
  fillColorChanged() {
    this.wbcontroller?.settingsDidChange(this.state.hud);
  }

  @Watch("$store.state.hud.fillColorForSticky")
  fillColorForStickyChanged() {
    this.wbcontroller?.settingsDidChange(this.state.hud);
  }

  @Watch("$store.state.hud.strokeWidth")
  strokeWidthChanged() {
    this.wbcontroller?.settingsDidChange(this.state.hud);
  }

  @Watch("$store.state.hud.tool")
  toolChanged(to: ToolType) {
    this.wbcontroller?.setToolFromString(to);
  }
}
</script>

<style lang="scss">
.Whiteboard {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: white;

  &.m-iframe {
    background-color: transparent;
  }

  &.m-black {
    background-color: black;
  }
}

.LoadingIndicator {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.FeedbackButton {
  position: fixed;
  right: var(--margin-small);
  bottom: var(--margin-small);
}

.wbroot {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 100px;
  right: 0;

  &.m-1 {
    right: 50%;
    border-right: 1px solid black;
  }

  &.m-2 {
    left: 50%;
  }

  &.m-loading {
    visibility: hidden;
  }

  @media (min-width: 768px) {
    bottom: 0;
    left: 50px;
    top: 50px;
  }
}
</style>
